import { ReactNode, useCallback, useContext } from 'react';
import { ValueOf } from '@cvent/carina/types/utility';
import { EngagementMessagesContext } from './EngagementMessages';
import { ENGAGEMENT_MESSAGE_TRIGGERS, ENGAGEMENT_PANEL_ACTIONS, ENGAGEMENT_PANEL_IDS } from './constants';

type EngagementMessage = Map<string, { badge: { panelId: typeof ENGAGEMENT_PANEL_IDS; publishedAt: Date } }>;

type ModeratorItem = {
  panelId: ValueOf<typeof ENGAGEMENT_PANEL_IDS>;
  publishedAt: Date;
};

type AgendaItem = {
  hasCombinedItems: boolean;
  panelId: ValueOf<typeof ENGAGEMENT_PANEL_IDS>;
  publishedAt: Date;
  dismissTimeout: number;
};

type Message = {
  image: ReactNode;
  body?: ReactNode;
  renderBody?: () => ReactNode;
  contentType: string;
  linkedContent?: {
    name?: ReactNode;
    renderName?: () => ReactNode;
    onDismiss?: () => void;
    onClick?: () => void;
  };
};

export type NotifyMessageFunction = (
  panelId: string,
  messageContent: {
    msgType: string;
    message?: Message;
    data?: any;
    analyticsData?: {
      id: string;
      sessionId: string;
      feature: ValueOf<typeof ENGAGEMENT_PANEL_IDS>;
      messageTrigger: ValueOf<typeof ENGAGEMENT_MESSAGE_TRIGGERS>;
    };
    onDismiss?: () => void;
    onSettingsClick?: () => void;
    onAlertClick?: () => void;
  }
) => void;

type UseEngagementMessageReturn = {
  panelIds: ValueOf<typeof ENGAGEMENT_PANEL_IDS>[];
  messages: EngagementMessage[];
  toastMessages: (ModeratorItem | AgendaItem)[];
  moderatorItem: ModeratorItem;
  agendaItem: AgendaItem;
  activePanel: ValueOf<typeof ENGAGEMENT_PANEL_IDS>;
  notifyMessage: NotifyMessageFunction;
  clearMessage;
  setActivePanel;
  toastContainerId;
  setToastContainerId;
};

/**
 * Hook that wraps the EngagementPanelMessengerContext and exposes setters
 * and state values so consumers can easily update and access state
 */
const useEngagementMessages = (): UseEngagementMessageReturn => {
  const { state = {}, dispatch } = useContext(EngagementMessagesContext);
  const { panelIds, messages, activePanel, moderatorItem, agendaItem, toastContainerId } = state;

  const setActivePanel = useCallback(
    panelId => {
      dispatch({
        type: ENGAGEMENT_PANEL_ACTIONS.SET_ACTIVE_PANEL,
        activePanel: panelId
      });
    },
    [dispatch]
  );

  const notifyMessage = useCallback(
    (
      panelId,
      {
        msgType,
        message = {},
        data = undefined,
        analyticsData = undefined,
        onDismiss = undefined,
        onSettingsClick = undefined,
        onAlertClick = undefined
      }
    ) => {
      dispatch({
        analyticsData,
        type: ENGAGEMENT_PANEL_ACTIONS.NOTIFY_MESSAGE,
        update: {
          panelId,
          payload: {
            type: msgType,
            message,
            data,
            onDismiss,
            onSettingsClick,
            onAlertClick
          }
        }
      });
    },
    [dispatch]
  );

  /**
   * Function to set the id of the dom in which the toasts will be portaled
   * @param {String} toastContainerId - id of the container dom
   */
  const setToastContainerId = useCallback(
    newToastContainerId => {
      dispatch({
        type: ENGAGEMENT_PANEL_ACTIONS.SET_TOAST_CONTAINER_ID,
        update: {
          toastContainerId: newToastContainerId
        }
      });
    },
    [dispatch]
  );

  const clearMessage = useCallback(
    (panelId, msgType) => {
      dispatch({
        type: ENGAGEMENT_PANEL_ACTIONS.CLEAR_MESSAGE,
        update: { panelId, msgType }
      });
    },
    [dispatch]
  );

  // Check if we have toast messages. messages is a Map structure
  const panelMessages = messages
    ? Object.values(Object.fromEntries(messages))
        .filter(item => item !== null && item.toasts !== null)
        .flatMap(item => item.toasts)
    : [];

  // We always want to show agenda items at the top priority (First toast), then the moderator toast, then the rest of the toasts
  const toastMessages = [agendaItem || null, moderatorItem || null, ...panelMessages].filter(
    message => message !== null
  );

  return {
    panelIds,
    messages,
    toastMessages,
    moderatorItem,
    agendaItem,
    activePanel,
    notifyMessage,
    clearMessage,
    setActivePanel,
    toastContainerId,
    setToastContainerId
  };
};

export default useEngagementMessages;
